import React, { useState, useEffect, useRef } from 'react'
import { MapPin } from 'lucide-react'
import { getExpandedMarkerPositions, groupComparableMarkers } from '../helpers/mapMarkerGrouping'

const getComparableMarkerIcon = (highlighted = false) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: highlighted ? 8 : 6,
  fillColor: '#3B82F6',
  fillOpacity: highlighted ? 1 : 0.8,
  strokeColor: highlighted ? '#F59E0B' : '#FFFFFF',
  strokeWeight: highlighted ? 4 : 2,
})

const getSubjectMarkerIcon = (grouped = false, highlighted = false) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: grouped ? 15 : highlighted ? 11 : 9,
  fillColor: '#EF4444',
  fillOpacity: 1,
  strokeColor: highlighted ? '#F59E0B' : '#FFFFFF',
  strokeWeight: highlighted ? 4 : 3,
})

const getGroupMarkerIcon = (count, containsSubject = false, highlighted = false) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: Math.min(18, 12 + Math.max(0, count - 2) * 0.7),
  fillColor: containsSubject ? '#EF4444' : '#3B82F6',
  fillOpacity: 1,
  strokeColor: highlighted ? '#F59E0B' : '#FFFFFF',
  strokeWeight: highlighted ? 4 : 3,
})

const getGroupMarkerLabel = (count, containsSubject = false) => ({
  text: `${containsSubject ? '+' : ''}${count}`,
  color: '#FFFFFF',
  fontSize: count > 9 ? '10px' : '11px',
  fontWeight: '700',
})

const ListingMap = ({
  listing = null,
  coordinates = null,
  address = '',
  onCoordinatesChange = null,
  isEditMode = false,
  loading = false,
  // Valuation-specific props
  valuation = null,
  showComparables = false,
  searchRadiusMeters = null,
  highlightedPropertyId = null,
  onComparableClick = null,
}) => {
  const mapRef = useRef(null)
  const comparableMarkersRef = useRef(new Map())
  const highlightedPropertyIdRef = useRef(highlightedPropertyId)
  const [map, setMap] = useState(null)
  const [geocoder, setGeocoder] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    highlightedPropertyIdRef.current = highlightedPropertyId
    const markerStates = new Map()
    comparableMarkersRef.current.forEach((entry, propertyId) => {
      const existing = markerStates.get(entry.marker) || { entry, highlighted: false }
      existing.highlighted = existing.highlighted || propertyId === highlightedPropertyId
      markerStates.set(entry.marker, existing)
    })

    markerStates.forEach(({ entry, highlighted }) => {
      if (entry.kind === 'group') {
        entry.marker.setIcon(getGroupMarkerIcon(entry.count, entry.containsSubject, highlighted))
        entry.marker.setLabel(getGroupMarkerLabel(entry.count, entry.containsSubject))
      } else {
        entry.marker.setIcon(getComparableMarkerIcon(highlighted))
      }
      entry.marker.setZIndex(highlighted ? 1000 : entry.containsSubject ? 900 : null)
    })
  }, [highlightedPropertyId])

  // Initialize Google Maps
  useEffect(() => {
    let isMounted = true
    let mapInstance = null

    const initializeMap = () => {
      if (!mapRef.current || !isMounted) return

      try {
        mapInstance = new window.google.maps.Map(mapRef.current, {
          center: { lat: 50.0755, lng: 14.4378 }, // Default to Prague
          zoom: 12,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        })

        const geocoderInstance = new window.google.maps.Geocoder()

        if (isMounted) {
          setMap(mapInstance)
          setGeocoder(geocoderInstance)
        }
      } catch (error) {
        console.error('Error creating map:', error)
        setError('Chyba při načítání mapy')
      }
    }

    if (window.google && window.google.maps) {
      initializeMap()
    } else {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDu8fNQeQGyby_XgC0JZLOPtqFN7SFkmuY`
      script.async = true
      script.onload = initializeMap
      document.body.appendChild(script)
    }

    return () => {
      isMounted = false
    }
  }, [])

  // Handle map marker placement when both map and data are ready
  useEffect(() => {
    if (!map) return

    let markers = []
    let circles = []
    let infoWindows = []
    let polylines = []
    let mapListeners = []
    let activeExpansion = null

    const cleanup = () => {
      markers.forEach((marker) => {
        try {
          if (marker && marker.setMap) {
            marker.setMap(null)
          }
        } catch (error) {
          console.warn('Error cleaning up marker:', error)
        }
      })
      circles.forEach((circle) => {
        try {
          if (circle && circle.setMap) {
            circle.setMap(null)
          }
        } catch (error) {
          console.warn('Error cleaning up circle:', error)
        }
      })
      polylines.forEach((polyline) => {
        try {
          polyline?.setMap?.(null)
        } catch (error) {
          console.warn('Error cleaning up map line:', error)
        }
      })
      mapListeners.forEach((listener) => {
        try {
          window.google.maps.event.removeListener(listener)
        } catch (error) {
          console.warn('Error cleaning up map listener:', error)
        }
      })
      infoWindows.forEach((infoWindow) => {
        try {
          if (infoWindow && infoWindow.close) {
            infoWindow.close()
          }
        } catch (error) {
          console.warn('Error cleaning up info window:', error)
        }
      })
      comparableMarkersRef.current.clear()
    }

    if (window.google && window.google.maps) {
      // For valuation mode (showing valuation data)
      console.log('valuation.ladder', listing, valuation)
      if (valuation && valuation.coordinatesLat && valuation.coordinatesLng) {
        const position = { lat: valuation.coordinatesLat, lng: valuation.coordinatesLng }
        map.setCenter(position)
        map.setZoom(15)

        // The valued property always owns the exact address position.
        const propertyMarker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: 'Hodnocená nemovitost',
          icon: getSubjectMarkerIcon(),
          zIndex: 900,
        })
        markers.push(propertyMarker)

        // Add info window for main property
        const propertyInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-size: 12px; font-family: Arial; max-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 4px; color: #EF4444;">Hodnocená nemovitost</div>
              <div>${valuation.address}</div>
              <div>${valuation.layout} - ${valuation.m2} m²</div>
              <div style="color: #666;">${valuation.condition}</div>
            </div>
          `,
        })
        infoWindows.push(propertyInfoWindow)

        const lastStep = valuation.ladderData?.[valuation.ladderData.length - 1]
        const circleRadiusMeters =
          Number(searchRadiusMeters) || Number(valuation.usedSearchRadiusMeters) || Number(valuation.searchRadiusMeters) || Number(lastStep?.radius)
        if (circleRadiusMeters) {
          const searchCircle = new window.google.maps.Circle({
            strokeColor: '#EF4444',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#EF4444',
            fillOpacity: 0.1,
            map: map,
            center: position,
            radius: circleRadiusMeters,
          })
          circles.push(searchCircle)
          map.fitBounds(searchCircle.getBounds(), 30)
        }

        // Similarity valuations provide ranked comparables directly. Legacy
        // ladder data remains a fallback only for old stored valuations.
        const comparableProperties = valuation.comparables?.length ? valuation.comparables : lastStep?.properties
        if (showComparables && comparableProperties?.length) {
          const groups = groupComparableMarkers(comparableProperties, position, valuation.address)
          const comparableInfoWindows = new Map()

          const getComparableInfoWindow = (property) => {
            if (comparableInfoWindows.has(property)) return comparableInfoWindows.get(property)
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px; font-size: 12px; font-family: Arial; max-width: 200px;">
                  <div style="font-weight: bold; margin-bottom: 4px;">${property.title}</div>
                  <div>${property.price?.toLocaleString() || 'N/A'} Kč</div>
                  <div>${property.m2Price?.toLocaleString() || 'N/A'} Kč/m²</div>
                  <div style="color: #666;">${property.location}</div>
                  <div style="color: #666;">${property.condition}</div>
                </div>
              `,
            })
            comparableInfoWindows.set(property, infoWindow)
            infoWindows.push(infoWindow)
            return infoWindow
          }

          const registerGroupMarker = (group, marker) => {
            group.comparables.forEach((property) => {
              const propertyId = property.propertyId?.toString()
              if (propertyId) {
                comparableMarkersRef.current.set(propertyId, {
                  marker,
                  kind: 'group',
                  count: group.comparables.length,
                  containsSubject: group.containsSubject,
                })
              }
            })
          }

          const collapseExpandedGroup = () => {
            if (!activeExpansion) return
            activeExpansion.markers.forEach((marker) => marker.setMap(null))
            activeExpansion.lines.forEach((line) => line.setMap(null))
            const { group, groupMarker } = activeExpansion
            if (group.containsSubject) {
              groupMarker.setIcon(getGroupMarkerIcon(group.comparables.length, true))
              groupMarker.setLabel(getGroupMarkerLabel(group.comparables.length, true))
              groupMarker.setTitle(`Hodnocená nemovitost · ${group.comparables.length} srovnání na stejné adrese`)
            } else {
              groupMarker.setMap(map)
            }
            registerGroupMarker(group, groupMarker)
            activeExpansion = null
          }

          const expandGroup = (group, groupMarker) => {
            if (activeExpansion?.group === group) {
              collapseExpandedGroup()
              return
            }
            collapseExpandedGroup()

            const origin = group.containsSubject ? position : group.position
            const expandedPositions = getExpandedMarkerPositions(origin, group.comparables.length)
            const expandedMarkers = []
            const expandedLines = []

            if (group.containsSubject) {
              groupMarker.setIcon(getSubjectMarkerIcon())
              groupMarker.setLabel(null)
              groupMarker.setTitle('Hodnocená nemovitost')
            } else {
              groupMarker.setMap(null)
            }

            group.comparables.forEach((property, index) => {
              const propertyId = property.propertyId?.toString() || null
              const highlighted = propertyId === highlightedPropertyIdRef.current
              const expandedPosition = expandedPositions[index]
              const line = new window.google.maps.Polyline({
                map,
                path: [origin, expandedPosition],
                strokeColor: '#64748B',
                strokeOpacity: 0.55,
                strokeWeight: 1.5,
                clickable: false,
              })
              const marker = new window.google.maps.Marker({
                position: expandedPosition,
                map,
                title: property.title,
                icon: getComparableMarkerIcon(highlighted),
                zIndex: highlighted ? 1000 : 800 + index,
              })
              marker.addListener('click', () => {
                if (onComparableClick) onComparableClick(property)
                else getComparableInfoWindow(property).open(map, marker)
              })
              markers.push(marker)
              polylines.push(line)
              expandedMarkers.push(marker)
              expandedLines.push(line)
              if (propertyId) comparableMarkersRef.current.set(propertyId, { marker, kind: 'comparable' })
            })

            activeExpansion = { group, groupMarker, markers: expandedMarkers, lines: expandedLines }
          }

          const subjectGroup = groups.find((group) => group.containsSubject)
          if (subjectGroup) {
            propertyMarker.setIcon(getGroupMarkerIcon(subjectGroup.comparables.length, true))
            propertyMarker.setLabel(getGroupMarkerLabel(subjectGroup.comparables.length, true))
            propertyMarker.setTitle(`Hodnocená nemovitost · ${subjectGroup.comparables.length} srovnání na stejné adrese`)
            registerGroupMarker(subjectGroup, propertyMarker)
            propertyMarker.addListener('click', () => expandGroup(subjectGroup, propertyMarker))
          } else {
            propertyMarker.addListener('click', () => propertyInfoWindow.open(map, propertyMarker))
          }

          groups.filter((group) => group !== subjectGroup).forEach((group) => {
            if (group.comparables.length === 1) {
              const property = group.comparables[0]
              const propertyId = property.propertyId?.toString() || null
              const highlighted = propertyId === highlightedPropertyIdRef.current
              const marker = new window.google.maps.Marker({
                position: group.position,
                map,
                title: property.title,
                icon: getComparableMarkerIcon(highlighted),
                zIndex: highlighted ? 1000 : null,
              })
              marker.addListener('click', () => {
                if (onComparableClick) onComparableClick(property)
                else getComparableInfoWindow(property).open(map, marker)
              })
              markers.push(marker)
              if (propertyId) comparableMarkersRef.current.set(propertyId, { marker, kind: 'comparable' })
              return
            }

            const marker = new window.google.maps.Marker({
              position: group.position,
              map,
              title: `${group.comparables.length} srovnání na stejné adrese`,
              icon: getGroupMarkerIcon(group.comparables.length),
              label: getGroupMarkerLabel(group.comparables.length),
            })
            marker.addListener('click', () => expandGroup(group, marker))
            markers.push(marker)
            registerGroupMarker(group, marker)
          })

          mapListeners.push(map.addListener('click', collapseExpandedGroup))
        } else {
          propertyMarker.addListener('click', () => propertyInfoWindow.open(map, propertyMarker))
        }
      }
      // For viewing mode (listing detail)
      else if (listing && listing.coordinates && listing.coordinates.lat && listing.coordinates.lng) {
        const position = { lat: listing.coordinates.lat, lng: listing.coordinates.lng }
        map.setCenter(position)
        map.setZoom(15)

        new window.google.maps.Marker({
          position: position,
          map: map,
          title: listing.address,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#EF4444',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
        })
      }
      // For edit mode (coordinates from props)
      else if (coordinates && coordinates.lat && coordinates.lng) {
        const position = { lat: coordinates.lat, lng: coordinates.lng }
        map.setCenter(position)
        map.setZoom(15)

        new window.google.maps.Marker({
          position: position,
          map: map,
          title: address,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#EF4444',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
        })
      }
    }

    return cleanup
  }, [map, listing, coordinates, address, valuation, showComparables, searchRadiusMeters, onComparableClick])

  const handleAddressSearch = async () => {
    if (!geocoder || !address.trim()) return

    try {
      const result = await geocoder.geocode({ address })

      if (result.results.length > 0) {
        const location = result.results[0].geometry.location
        const coords = { lat: location.lat(), lng: location.lng() }

        if (onCoordinatesChange) {
          onCoordinatesChange(coords)
        }

        if (map) {
          map.setCenter(coords)
          map.setZoom(15)

          // Add marker
          new window.google.maps.Marker({
            position: coords,
            map: map,
            title: address,
          })
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error)
      setError('Nepodařilo se najít adresu')
    }
  }

  return (
    <div className="space-y-4 h-full flex">
      {isEditMode && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Mapa</h3>
          <p className="text-sm text-gray-600 mb-4">Klikněte na ikonu mapy vedle adresy pro zobrazení lokace na mapě.</p>
        </div>
      )}

      <div ref={mapRef} className="w-full h-full min-h-64" />

      {isEditMode && coordinates && coordinates.lat && coordinates.lng && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <strong>Adresa nalezena:</strong> {address}
          <br />
          <small>
            Souřadnice: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
          </small>
        </div>
      )}

      {isEditMode && (
        <div className="flex gap-2">
          <input type="text" value={address} placeholder="Zadejte adresu" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" disabled />
          <button
            type="button"
            onClick={handleAddressSearch}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            <MapPin size={16} />
          </button>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
    </div>
  )
}

export default ListingMap
